import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { supabase } from "@/integrations/supabase/client";
import { validateScan, recordPickSerial, recordPackSerial, assertShipReadiness } from "@/lib/traceability.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, ScanLine, ShieldCheck, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/trace/capture")({
  component: TraceCapture,
  head: () => ({ meta: [{ title: "Trace capture — CairnOS" }] }),
});

type Stage = "receive" | "pick" | "pack";

function TraceCapture() {
  const { tenantId, customerId } = useScope();
  const validate = useServerFn(validateScan);
  const recPick = useServerFn(recordPickSerial);
  const recPack = useServerFn(recordPackSerial);
  const ready = useServerFn(assertShipReadiness);

  const [stage, setStage] = useState<Stage>("pick");
  const [orderNum, setOrderNum] = useState("");
  const [orderId, setOrderId] = useState<string | null>(null);
  const [skuId, setSkuId] = useState<string | null>(null);
  const [serial, setSerial] = useState("");
  const [lot, setLot] = useState("");
  const [expiry, setExpiry] = useState("");
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scans, setScans] = useState<{ ts: string; outcome: string; reason: string | null; serial: string; lot: string }[]>([]);
  const [readiness, setReadiness] = useState<any>(null);

  // Load order by number → find allocations
  const lookupOrder = async () => {
    if (!orderNum.trim() || !tenantId) return;
    const { data } = await supabase.from("orders")
      .select("id, order_number, customer_id, status").eq("tenant_id", tenantId)
      .eq("order_number", orderNum.trim()).maybeSingle();
    if (!data) { toast.error("Order not found"); setOrderId(null); return; }
    setOrderId(data.id);
    toast.success(`Order ${data.order_number} (${data.status})`);
  };

  const { data: allocs = [] } = useQuery({
    queryKey: ["trace-allocs", orderId],
    enabled: !!orderId,
    queryFn: async () => {
      const { data } = await supabase.from("allocations")
        .select("id, sku_id, qty, lot, status, skus(id, sku_code, name)").eq("order_id", orderId!);
      return data ?? [];
    },
  });

  const submit = useMutation({
    mutationFn: async () => {
      if (!tenantId || !customerId || !skuId) throw new Error("Pick a SKU first");
      const r = await validate({ data: {
        tenantId, customerId, skuId, stage,
        serial: serial.trim() || null,
        lot: lot.trim() || null,
        expiryDate: expiry || null,
        orderId: orderId ?? null,
        override,
      } });
      setScans((s) => [{ ts: new Date().toISOString(), outcome: r.outcome, reason: r.reason, serial, lot }, ...s].slice(0, 50));
      if (r.blocking) throw new Error(r.reason ?? "Scan blocked");

      // On pick/pack, also persist linkage when serial present
      if (serial.trim() && (stage === "pick" || stage === "pack")) {
        const alloc = (allocs as any[]).find((a) => a.sku_id === skuId);
        if (!alloc) throw new Error("No allocation for this SKU on order");
        // Resolve serial_id
        const { data: sr } = await supabase.from("serials").select("id").eq("tenant_id", tenantId).eq("sku_id", skuId).eq("serial_value", serial.trim()).maybeSingle();
        if (!sr) throw new Error("Serial not found in inventory — receive it first");
        if (stage === "pick") {
          await recPick({ data: { tenantId, allocationId: alloc.id, serialId: (sr as any).id } });
        } else {
          // pack: needs a package item — pick most recent for this SKU on this order
          const { data: pkg } = await supabase.from("shipment_packages")
            .select("id, shipment_id, shipments!inner(order_id)")
            .eq("shipments.order_id", orderId!).order("created_at", { ascending: false }).limit(1).maybeSingle();
          if (!pkg) throw new Error("No package on this order yet");
          const { data: item } = await supabase.from("shipment_package_items")
            .select("id").eq("shipment_package_id", (pkg as any).id).eq("sku_id", skuId).is("serial_id", null).limit(1).maybeSingle();
          if (!item) throw new Error("No unfilled package item for this SKU");
          await recPack({ data: { tenantId, packageItemId: (item as any).id, serialId: (sr as any).id, shipmentId: (pkg as any).shipment_id } });
        }
      }
      return r;
    },
    onSuccess: () => { toast.success("Scan recorded"); setSerial(""); },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const checkReady = async () => {
    if (!tenantId || !orderId) return;
    setBusy(true);
    try {
      const r = await ready({ data: { tenantId, orderId } });
      setReadiness(r);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-5xl">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2"><ScanLine className="h-4 w-4 text-primary" /> Trace capture</h2>
        <p className="text-sm text-muted-foreground">Scan serials, lots, or expirations at any workflow stage. Enforces tenant/customer/SKU traceability policy and records to the audit log.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-sm">Context</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-[120px_1fr_auto] gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs">Stage</Label>
            <Select value={stage} onValueChange={(v) => setStage(v as Stage)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{(["receive","pick","pack"] as Stage[]).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Order #</Label>
            <div className="flex gap-2">
              <Input value={orderNum} onChange={(e) => setOrderNum(e.target.value)} placeholder="ORD-1234" onKeyDown={(e) => e.key === "Enter" && lookupOrder()} />
              <Button variant="outline" onClick={lookupOrder}>Load</Button>
            </div>
          </div>
          <div>
            <Button onClick={checkReady} variant="secondary" disabled={!orderId || busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Ship readiness
            </Button>
          </div>
        </CardContent>
      </Card>

      {orderId && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Order Allocations</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Qty</TableHead><TableHead>Lot</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {(allocs as any[]).map((a) => (
                  <TableRow key={a.id} className={skuId === a.sku_id ? "bg-accent/40" : ""}>
                    <TableCell className="font-mono text-xs">{a.skus?.sku_code}</TableCell>
                    <TableCell>{a.qty}</TableCell>
                    <TableCell className="font-mono text-xs">{a.lot ?? "—"}</TableCell>
                    <TableCell><Badge variant="outline">{a.status}</Badge></TableCell>
                    <TableCell><Button size="sm" variant={skuId === a.sku_id ? "default" : "ghost"} onClick={() => setSkuId(a.sku_id)}>Select</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-sm">Capture</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Serial</Label><Input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="Scan serial…" autoFocus /></div>
          <div className="space-y-1.5"><Label className="text-xs">Lot</Label><Input value={lot} onChange={(e) => setLot(e.target.value)} placeholder="LOT-…" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Expiry</Label><Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} /></div>
          <div className="md:col-span-3 flex items-center justify-between">
            <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} /> Override (supervisor)</label>
            <Button onClick={() => submit.mutate()} disabled={submit.isPending || !skuId || (!serial && !lot && !expiry)}>
              {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Record scan
            </Button>
          </div>
        </CardContent>
      </Card>

      {readiness && (
        <Card>
          <CardHeader><CardTitle className="text-sm flex items-center gap-2">
            {readiness.ready ? <ShieldCheck className="h-4 w-4 text-success" /> : <AlertTriangle className="h-4 w-4 text-destructive" />}
            Ship readiness: {readiness.ready ? "OK" : `${readiness.issues.length} blocker(s)`}
          </CardTitle></CardHeader>
          <CardContent>
            {readiness.ready ? <p className="text-sm text-muted-foreground">All required traces captured.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Mode</TableHead><TableHead>Captured</TableHead><TableHead>Expected</TableHead><TableHead>Missing</TableHead></TableRow></TableHeader>
                <TableBody>
                  {readiness.issues.map((i: any, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono text-xs">{i.sku_code}</TableCell>
                      <TableCell><Badge variant="destructive">{i.mode}</Badge></TableCell>
                      <TableCell>{i.captured}</TableCell>
                      <TableCell>{i.expected}</TableCell>
                      <TableCell className="font-bold text-destructive">{i.missing}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-sm">Recent Scans</CardTitle></CardHeader>
        <CardContent>
          {scans.length === 0 ? <p className="text-xs text-muted-foreground">No scans this session.</p> : (
            <Table>
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Outcome</TableHead><TableHead>Serial</TableHead><TableHead>Lot</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
              <TableBody>
                {scans.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">{new Date(s.ts).toLocaleTimeString()}</TableCell>
                    <TableCell><Badge variant={s.outcome === "fail" ? "destructive" : s.outcome === "warn" ? "secondary" : "default"}>{s.outcome}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{s.serial || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{s.lot || "—"}</TableCell>
                    <TableCell className="text-xs">{s.reason ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}